import {isNull, uniqBy} from 'lodash-es';
import {type AxiosRequestConfig} from 'axios';
import {asDecimalCurrency, asInteger} from 'utils/number-format';
import {reportingPost} from 'api';
import {fullMonthYearFormat} from 'utils/date-time';
import dayjs from 'utils/dayjs';
import {sendSentryException} from 'utils/error-handling';
import {
    type FormattedGraphData,
    type GetAccountingDashboardReportResponse,
    type GetAccountingDashboardReportSummary,
    type GetMtdAccountingReportBaseRequest,
    type GetMTDAggregatedReportRawResponse,
    type GetMTDAggregatedReportResponse,
    type GetMTDFacilitiesBreakdownReportRawResponse,
    type GetMTDFacilitiesBreakdownReportResponse,
} from 'api/accounting/accounting.types';
import {camelizeKeys} from 'utils/format-strings';

/**
 * formats the revenue data for the MTD aggregated graph
 * @param  data - The response data to format
 * @param  lastMonth - The name of the previous month
 * @param  currentMonth - The name of the current month
 * @returns the revenue data formatted for the MTD aggregated graph
 *
 * @group DTO Formatters
 */
const formatMTDAggregatedRevenue = (
    data: GetMTDAggregatedReportResponse,
    lastMonth: string,
    currentMonth: string
) => {
    const formattedRevenueData: FormattedGraphData[] = data
        .reduce(
            (acc, revenueResponseData, index) => {
                const {
                    date: currentDateLabel,
                    previous: {
                        date: previousDateLabel,
                        remit: {mtd: previousRemitMTD},
                    },
                    remit: {mtd: currentRemitMTD},
                } = revenueResponseData;

                acc[0].data.push({
                    id: previousDateLabel,
                    x: index + 1,
                    y: previousRemitMTD ? previousRemitMTD / 100 : 0,
                });

                // As long as the data points for the current month DO NOT contain `null` values, add them to the data series for the current month.
                if (!isNull(currentRemitMTD)) {
                    acc[1].data.push({
                        id: currentDateLabel,
                        x: index + 1,
                        y: !isNull(currentRemitMTD)
                            ? currentRemitMTD / 100
                            : currentRemitMTD,
                    });
                }

                return acc;
            },
            [
                {id: lastMonth, data: []},
                {id: currentMonth, data: []},
            ]
        )
        .map((dataSet) => ({
            ...dataSet,
            data: uniqBy(dataSet.data, 'id'),
        }));

    return formattedRevenueData;
};

/**
 * formats the reservations data for the MTD aggregated graph
 * @param data - The response data to format
 * @param lastMonth - The name of the previous month
 * @param currentMonth - The name of the current month
 * @returns the reservation data formatted for the MTD aggregated graph
 *
 * @group DTO Formatters
 */
const formatMTDAggregatedReservations = (
    data: GetMTDAggregatedReportResponse,
    lastMonth: string,
    currentMonth: string
) => {
    const formattedReservationsData: FormattedGraphData[] = data
        .reduce(
            (acc, reservationsResponseData, index) => {
                const {
                    date: currentDateLabel,
                    previous: {
                        date: previousDateLabel,
                        transactions: {mtd: previousTransactionsMTD},
                    },
                    transactions: {mtd: currentTransactionsMTD},
                } = reservationsResponseData;

                acc[0].data.push({
                    id: previousDateLabel,
                    x: index + 1,
                    y: previousTransactionsMTD || 0,
                });

                // As long as the data points for the current month DO NOT contain `null` values, add them to the data series for the current month.
                if (!isNull(currentTransactionsMTD)) {
                    acc[1].data.push({
                        id: currentDateLabel,
                        x: index + 1,
                        y: currentTransactionsMTD || 0,
                    });
                }

                return acc;
            },
            [
                {id: lastMonth, data: []},
                {id: currentMonth, data: []},
            ]
        )
        .map((dataSet) => ({
            ...dataSet,
            data: uniqBy(dataSet.data, 'id'),
        }));

    return formattedReservationsData;
};

/**
 * Formats the response from the dashboard endpoint
 * @param  response - The response data to format
 * @param  dateTo - The dateTo parameter representing the end of the date range queried
 *
 * @group DTO Formatters
 */
const formatMTDAggregatedReport = (
    response: GetMTDAggregatedReportResponse,
    dateTo: GetMtdAccountingReportBaseRequest['dateTo']
) => {
    const currentMonth = dayjs(dateTo).format(fullMonthYearFormat);
    const lastMonth = dayjs(dateTo)
        .subtract(1, 'month')
        .format(fullMonthYearFormat);

    const formattedResponse = {
        revenue: formatMTDAggregatedRevenue(response, lastMonth, currentMonth),
        reservations: formatMTDAggregatedReservations(
            response,
            lastMonth,
            currentMonth
        ),
    };

    return formattedResponse;
};

/**
 * Formats the request data for the dashboard yoy summary api endpoints
 * @param  response - The data to format
 *
 * @group DTO Formatters
 */
export const formatGetMtdSummary = (
    response: GetAccountingDashboardReportResponse['mtd']
): GetAccountingDashboardReportSummary => {
    const formattedResponse = {
        revenue: {
            value: asDecimalCurrency(response?.remit.mtd / 100),
            previousValue: asDecimalCurrency(
                (response?.previous.remit.mtd || 0) / 100
            ),
            diff: response?.remit.change,
        },
        reservations: {
            value: asInteger(response?.transactions.mtd),
            previousValue: asInteger(response?.previous.transactions.mtd || 0),
            diff: response?.transactions.change,
        },
    };

    return formattedResponse;
};

/**
 * Gets the dashboard (summary cards) accounting data for a collection of facilities
 * @param  data - The parameters for the report
 * @param config - The axios config
 *
 * @see '/control-panel-service/v1/accounting/dashboard/'
 */
export const getMtdSummary = async (
    data: GetMtdAccountingReportBaseRequest,
    config: AxiosRequestConfig = {}
) => {
    try {
        const response = await reportingPost<
            GetAccountingDashboardReportResponse,
            GetMtdAccountingReportBaseRequest
        >('dashboard/', data, config);

        const formattedResponse = formatGetMtdSummary(response.data.data.mtd);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Gets a collection of aggregated MTD reports for a given set of facilities
 * @param data - The parameters for the report
 * @param config - The axios config
 *
 * @see '/control-panel-service/v1/accounting/facilities/mtd/aggregated/'
 */
export const getMTDAggregatedReport = async (
    data: GetMtdAccountingReportBaseRequest,
    config: AxiosRequestConfig = {}
) => {
    const {dateTo} = data;

    try {
        const response = (await reportingPost<
            never,
            GetMtdAccountingReportBaseRequest
        >(
            'facilities/mtd/aggregated/',
            data,
            config
        )) as GetMTDAggregatedReportRawResponse;

        const formattedResponse = formatMTDAggregatedReport(
            response.data.data,
            dateTo
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Formats a given MTD (Month-To-Date) report response by camelizing the
 * keys and rounding the current rating values to one decimal place.
 *
 * @param response - The original response object to be formatted.
 * @returns The formatted response with camelized keys and rating values rounded to one decimal place.
 *
 * @group DTO Formatters
 */
const formatMTDReportResponse = (
    response: GetMTDFacilitiesBreakdownReportRawResponse
) => {
    const formattedResponse = camelizeKeys(response);

    const formattedResults = formattedResponse.data.results.map((data) => ({
        ...data,
        currentRating: data.currentRating,
    }));

    const formattedData = {
        data: {
            results: formattedResults,
        },
        meta: formattedResponse.meta,
    };

    return formattedData;
};

/**
 * Gets an MTD (breakdown table) report for a given set of facilities
 * @param data - The parameters for the report
 * @param config - The axios config
 *
 * @see '/control-panel-service/v1/accounting/facilities/mtd/'
 */
export const getMTDReport = async (
    data: GetMtdAccountingReportBaseRequest,
    config: AxiosRequestConfig = {}
) => {
    try {
        const response = await reportingPost<
            GetMTDFacilitiesBreakdownReportResponse[],
            GetMtdAccountingReportBaseRequest
        >('facilities/mtd/', data, config);
        const formattedResponse = formatMTDReportResponse(response.data);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};
